import React, { useContext } from 'react';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import { formatDateString } from '@/utils/helper';
import { Button, Typography, useToast } from '@pw-tech/omni-ui';
import { Check, Clock, LaptopMinimal, NotebookPenIcon } from 'lucide-react';
import FooterPortal from '@/modules/common/Portals/FooterPortal/FooterPortal';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { SOM_SYLLABUS, SOT_SYLLABUS } from '@/modules/common/constants';
const ExamScheduled = () => {
  const {
    registrationDetails
  } = useContext(RegistrationContext);
  const {
    selectedCourseData
  } = useContext(CourseContext);
  const {
    toast: showToaster
  } = useToast();
  const downloadSyllabus = () => {
    if (selectedCourseData?.batchCrmName.includes('SOT')) {
      window.open(SOT_SYLLABUS, '_blank');
    } else if (selectedCourseData?.batchCrmName.includes('SOB')) {
      window.open(SOM_SYLLABUS, '_blank');
    } else {
      showToaster({
        message: 'Syllabus not available for this course',
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        }
      });
      return;
    }
    showToaster({
      message: 'Syllabus downloaded successfully',
      variant: 'success',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center'
      }
    });
  };
  const handleMettlRedirect = (link: string) => {
    if (!link) {
      showToaster({
        message: 'Link not available',
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        }
      });
      return;
    }
    window.open(link, '_blank');
  };
  return <article data-sentry-component="ExamScheduled" data-sentry-source-file="ExamScheduled.tsx">
      <Typography variant="heading3" weight="semi-bold" data-sentry-element="Typography" data-sentry-source-file="ExamScheduled.tsx">
        Schedule Your Entrance Exam (CEE)
      </Typography>
      <div className="flex gap-2">
        <Typography variant="regular" component="p" className="flex items-center gap-1 p-2 rounded-lg bg-[#F8F8F8] !text-xs" data-sentry-element="Typography" data-sentry-source-file="ExamScheduled.tsx">
          <NotebookPenIcon size={16} data-sentry-element="NotebookPenIcon" data-sentry-source-file="ExamScheduled.tsx" />
          {selectedCourseData?.metaData?.totalQuestions || 46} questions
        </Typography>
        <Typography variant="regular" component="p" className="flex items-center gap-1 p-2 rounded-lg bg-[#F8F8F8] !text-xs" data-sentry-element="Typography" data-sentry-source-file="ExamScheduled.tsx">
          <Clock size={16} data-sentry-element="Clock" data-sentry-source-file="ExamScheduled.tsx" />
          {selectedCourseData?.metaData?.totalTime || 90} minutes
        </Typography>
        <Typography variant="regular" component="p" className="flex items-center gap-1 p-2 rounded-lg bg-[#F8F8F8] !text-xs" data-sentry-element="Typography" data-sentry-source-file="ExamScheduled.tsx">
          <LaptopMinimal size={16} data-sentry-element="LaptopMinimal" data-sentry-source-file="ExamScheduled.tsx" />
          {selectedCourseData?.metaData?.examMode || 'Online'}
        </Typography>
      </div>
      <div className="bg-[#EDFCF2] border-[#ADCFB7] border rounded-lg p-4 py-2 mt-4 flex flex-col">
        <Typography data-sentry-element="Typography" data-sentry-source-file="ExamScheduled.tsx">
          Exam Scheduled for{' '}
          <b>{formatDateString(registrationDetails?.dateOfExamination, 'MMM d, yyyy')}</b>
        </Typography>
        <Typography variant="regular" className="!text-sm" data-sentry-element="Typography" data-sentry-source-file="ExamScheduled.tsx">
          Please login to the portal prior to the scheduled time
        </Typography>
      </div>
      <div className="mt-4 p-2 sm:!p-4 bg-[#F8F8F8] rounded-lg">
        <div>
          <Typography variant="heading4" weight="semi-bold" data-sentry-element="Typography" data-sentry-source-file="ExamScheduled.tsx">
            Important Instructions
          </Typography>
          <div className="pt-2 sm:!pt-[10px] flex flex-col gap-1">
            <div className="flex gap-1">
              <div className="flex items-center justify-center flex-shrink-0 w-5 h-5">
                <Check size={20} color="#3D3D3D" data-sentry-element="Check" data-sentry-source-file="ExamScheduled.tsx" />
              </div>
              <Typography className="!text-sm" data-sentry-element="Typography" data-sentry-source-file="ExamScheduled.tsx">
                The CEE exam will be held online, so please ensure a stable internet connection.
              </Typography>
            </div>
            <div className="flex gap-1">
              <div className="flex items-center justify-center flex-shrink-0 w-5 h-5">
                <Check size={20} color="#3D3D3D" data-sentry-element="Check" data-sentry-source-file="ExamScheduled.tsx" />
              </div>
              <Typography className="!text-sm" data-sentry-element="Typography" data-sentry-source-file="ExamScheduled.tsx">
                Syllabus, exam pattern, and mock test link are provided above — prepare well and
                give it your best.
              </Typography>
            </div>
            <div className="flex gap-1">
              <div className="flex items-center justify-center flex-shrink-0 w-5 h-5">
                <Check size={20} color="#3D3D3D" data-sentry-element="Check" data-sentry-source-file="ExamScheduled.tsx" />
              </div>
              <Typography className="!text-sm" data-sentry-element="Typography" data-sentry-source-file="ExamScheduled.tsx">
                For any issues during the exam, feel free to contact our support team.
              </Typography>
            </div>
          </div>
        </div>
        <div className="flex sm:!gap-4 pt-5">
          <Button type="button" variant="lowFocus" className="!text-[#196CDC]" onClick={() => handleMettlRedirect(registrationDetails?.mockTestUrl || '')} data-sentry-element="Button" data-sentry-source-file="ExamScheduled.tsx">
            Attempt Mock test
          </Button>
          <Button type="button" variant="link" className="!bg-[#F8F8F8]" onClick={() => downloadSyllabus()} data-sentry-element="Button" data-sentry-source-file="ExamScheduled.tsx">
            Download Full Syllabus
          </Button>
        </div>
      </div>

      <div className="py-2">
        <FooterPortal label="Login to Test Portal" type="button" btnWrapperClass="relative sm:left-[31%]" onClick={() => handleMettlRedirect(registrationDetails?.testUrl ?? '')} data-sentry-element="FooterPortal" data-sentry-source-file="ExamScheduled.tsx" />
      </div>
    </article>;
};
export default ExamScheduled;